import { useEffect, useMemo, useState } from 'react';
import { TIPOLOGIA_CATEGORIES } from './FilterPanel';

/* ------------------------------------------------------------------ *
 * Filtri B2C stile immobiliare.it: una riga di pill-CATEGORIA collassate
 * ("Verdetto", "Prezzo", "Tipologia", …). Cliccando una pill si apre un
 * bottom-sheet con le opzioni; la scelta resta scritta sulla pill.
 * Niente verdetti "esplosi": Verdetto è UNA pill che apre il selettore.
 * Marina 2026-07-11.
 * ------------------------------------------------------------------ */

// Tutti i verdetti, singoli. Colori coerenti con i marker della mappa.
// Mostrati SEMPRE, anche quelli non ancora scoperti dal prospect. Marina 2026-07-11.
const VERDETTO_GROUPS = [
  { id: 'tutti',       label: 'Tutti',       keys: [],              color: '#5A5E64' },
  // Marina 2026-09-03: filtro demo — mostra SOLO gli immobili con analisi già
  // pronta (url_report valorizzato), così in demo non si aspettano le analisi.
  { id: 'analizzate',  label: 'Già analizzati', keys: ['analizzate'], color: '#2563eb' },
  { id: 'eccezionale', label: 'Eccezionale', keys: ['eccezionale'], color: '#16a34a', star: true },
  { id: 'consigliata', label: 'Consigliata', keys: ['consigliata'], color: '#65a30d' },
  { id: 'con_riserva', label: 'Con riserva', keys: ['con_riserva'], color: '#d97706' },
  { id: 'da_evitare',  label: 'Da evitare',  keys: ['da_evitare'],  color: '#dc2626' },
];

export const FILTER_GROUPS = VERDETTO_GROUPS;

export function findGroupForKeys(verdettoSet) {
  if (!verdettoSet || verdettoSet.size === 0) return VERDETTO_GROUPS[0]; // tutti
  for (const g of VERDETTO_GROUPS) {
    if (g.keys.length === 0) continue;
    if (g.keys.length === verdettoSet.size && g.keys.every((k) => verdettoSet.has(k))) return g;
  }
  return null;
}

const euro = (v) => {
  try { return '€ ' + new Intl.NumberFormat('it-IT').format(v); } catch { return '€ ' + v; }
};

const PREZZO_OPTS = [50000, 100000, 150000, 200000, 300000, 500000]
  .map((v) => ({ v: String(v), label: 'fino a ' + euro(v) }));
const MQ_OPTS = [40, 50, 60, 80, 100, 120]
  .map((v) => ({ v: String(v), label: 'da ' + v + ' m²' }));

// Normalizza opzioni da filterOptions: possono essere stringhe o {value,label}.
const toOpts = (arr) =>
  (arr || []).map((x) =>
    x && typeof x === 'object'
      ? { v: String(x.value ?? x.id ?? x), label: String(x.label ?? x.value ?? x) }
      : { v: String(x), label: String(x) });

export default function ProspectFilters({
  searchParams,
  setSearchParams,
  filterOptions,
  discoveredVerdetti,
  onVerdettoBlocked,
}) {
  const [openKey, setOpenKey] = useState(null); // categoria col sheet aperto

  // Chiudi il sheet con ESC
  useEffect(() => {
    if (!openKey) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpenKey(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openKey]);

  const params = searchParams || new URLSearchParams();

  const getMulti = (key) => {
    const v = params.get(key);
    return v ? v.split(',').filter(Boolean) : [];
  };
  const setSingle = (key, value) => {
    const next = new URLSearchParams(params);
    if (value == null || value === '') next.delete(key);
    else next.set(key, String(value));
    setSearchParams(next, { replace: true });
  };
  const setMulti = (key, values) => {
    const next = new URLSearchParams(params);
    if (!values || values.length === 0) next.delete(key);
    else next.set(key, values.join(','));
    setSearchParams(next, { replace: true });
  };

  // Costruzione dinamica delle categorie (le ultime dipendono da filterOptions)
  const FILTERS = useMemo(() => {
    const fo = filterOptions || {};
    return [
      { key: 'verdetto',                 label: 'Verdetto',       type: 'verdetto' },
      { key: 'prezzo_max',               label: 'Prezzo',         type: 'single', opts: PREZZO_OPTS },
      { key: 'scade_entro',              label: 'Scade entro',    type: 'date' },
      { key: 'mq_min',                   label: 'Superficie',     type: 'single', opts: MQ_OPTS },
      { key: 'tipologia_categoria',      label: 'Tipologia',      type: 'multi',  opts: TIPOLOGIA_CATEGORIES.map((t) => ({ v: t.id, label: t.label })) },
      fo.comune?.length                ? { key: 'comune',        label: 'Comune',        type: 'multi', opts: toOpts(fo.comune) } : null,
      fo.tribunale?.length             ? { key: 'tribunale',     label: 'Tribunale',     type: 'multi', opts: toOpts(fo.tribunale) } : null,
      fo.piano_categoria?.length       ? { key: 'piano_categoria', label: 'Piano',       type: 'multi', opts: toOpts(fo.piano_categoria) } : null,
      fo.livello_ristrutturazione?.length ? { key: 'livello_ristrutturazione', label: 'Stato', type: 'multi', opts: toOpts(fo.livello_ristrutturazione) } : null,
      fo.tentativo_vendita?.length     ? { key: 'tentativo_vendita', label: 'Tentativo', type: 'multi', opts: (fo.tentativo_vendita || []).map((v) => ({ v: String(v), label: v + '°' })) } : null,
      fo.disponibilita?.length         ? { key: 'disponibilita', label: 'Disponibilità', type: 'multi', opts: toOpts(fo.disponibilita) } : null,
    ].filter(Boolean);
  }, [filterOptions]);

  // Verdetto attivo (per label + pallino colore)
  const verdettoSet = useMemo(() => {
    const v = params.get('verdetto');
    return new Set(v ? v.split(',').map((s) => s.trim()).filter(Boolean) : []);
  }, [params.get('verdetto')]); // eslint-disable-line react-hooks/exhaustive-deps
  const verdettoGroup = findGroupForKeys(verdettoSet);

  // Stato "attivo" + etichetta della pill per ogni categoria
  const pillState = (f) => {
    if (f.type === 'verdetto') {
      const active = !!verdettoGroup && verdettoGroup.id !== 'tutti';
      return { active, label: active ? verdettoGroup.label : 'Verdetto', dot: active ? verdettoGroup.color : null };
    }
    if (f.type === 'single') {
      const val = params.get(f.key);
      if (!val) return { active: false, label: f.label };
      const o = f.opts.find((x) => x.v === val);
      return { active: true, label: o ? o.label.replace(/^fino a |^da /, '') : f.label };
    }
    if (f.type === 'date') {
      const val = params.get(f.key);
      if (!val) return { active: false, label: f.label };
      const d = new Date(val + 'T00:00:00');
      const lbl = Number.isNaN(d.getTime()) ? val : d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
      return { active: true, label: 'entro ' + lbl };
    }
    // multi
    const vals = getMulti(f.key);
    if (vals.length === 0) return { active: false, label: f.label };
    if (vals.length === 1) {
      const o = f.opts.find((x) => x.v === vals[0]);
      return { active: true, label: o ? o.label : f.label };
    }
    return { active: true, label: `${f.label} · ${vals.length}` };
  };

  // Applica scelta VERDETTO — tutti i verdetti selezionabili, anche non scoperti.
  const applyVerdetto = (g) => {
    if (g.id === 'tutti' || g.keys.length === 0) setSingle('verdetto', '');
    else setSingle('verdetto', g.keys.join(','));
    setOpenKey(null);
  };

  const openFilter = FILTERS.find((f) => f.key === openKey);

  return (
    <div style={{ position: 'relative' }}>
      <div className="pr-filters-scroll" style={{
        display: 'flex', gap: 8, overflowX: 'auto',
        // Sfonda il padding orizzontale del .pr-topbar (12px) così lo slider
        // scorre a filo dello schermo invece di sparire dietro il bordo.
        margin: '0 -12px', padding: '2px 12px 8px', scrollPaddingLeft: 12,
        scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
      }}>
        <style>{'.pr-filters-scroll::-webkit-scrollbar{display:none}'}</style>
        {FILTERS.map((f) => {
          const st = pillState(f);
          return (
            <button
              key={f.key}
              onClick={() => setOpenKey(f.key)}
              style={{
                flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
                fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap',
                fontFamily: 'Manrope, system-ui, sans-serif',
                border: `1.5px solid ${st.active ? '#1F2937' : '#E4E6EA'}`,
                background: st.active ? '#1F2937' : '#fff',
                color: st.active ? '#fff' : '#3A3F45',
                boxShadow: st.active ? '0 2px 10px rgba(0,0,0,0.14)' : 'none',
                transition: 'background .15s, color .15s, border-color .15s',
              }}
            >
              {st.dot && (
                <span style={{ width: 8, height: 8, borderRadius: '50%', flex: '0 0 auto', background: st.dot }} />
              )}
              <span>{st.label}</span>
              <span style={{ opacity: 0.55, fontSize: 10, marginLeft: 1 }}>{'▼'}</span>
            </button>
          );
        })}
      </div>

      {openFilter && (
        <FilterSheet
          filter={openFilter}
          params={params}
          verdettoGroup={verdettoGroup}
          getMulti={getMulti}
          setSingle={setSingle}
          setMulti={setMulti}
          applyVerdetto={applyVerdetto}
          onClose={() => setOpenKey(null)}
        />
      )}
    </div>
  );
}

/* ---------------- Bottom sheet selettore ---------------- */
function FilterSheet({ filter, params, verdettoGroup, getMulti, setSingle, setMulti, applyVerdetto, onClose }) {
  const isMulti = filter.type === 'multi';
  const singleVal = filter.type === 'single' ? params.get(filter.key) : null;
  const multiVals = isMulti ? getMulti(filter.key) : [];

  const toggleMulti = (v) => {
    const cur = getMulti(filter.key);
    const i = cur.indexOf(v);
    const next = [...cur];
    if (i >= 0) next.splice(i, 1); else next.push(v);
    setMulti(filter.key, next);
  };

  const rows = filter.type === 'verdetto' ? VERDETTO_GROUPS : filter.opts;

  return (
    <>
      {/* backdrop */}
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.38)', zIndex: 4000,
      }} />
      {/* sheet */}
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 4001,
        background: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20,
        boxShadow: '0 -8px 40px rgba(0,0,0,0.22)', maxHeight: '72vh',
        display: 'flex', flexDirection: 'column',
        fontFamily: 'Manrope, system-ui, sans-serif',
        animation: 'prSheetUp .22s cubic-bezier(.22,.61,.36,1)',
      }}>
        <style>{'@keyframes prSheetUp{from{transform:translateY(100%)}to{transform:translateY(0)}}'}</style>
        {/* grabber */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
          <span style={{ width: 40, height: 5, borderRadius: 3, background: '#DDE1E6' }} />
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '2px 18px 10px',
        }}>
          <span style={{ fontSize: 17, fontWeight: 800, color: '#1F2937' }}>{filter.label}</span>
          <button onClick={onClose} aria-label="Chiudi" style={{
            border: 0, background: '#F1F3F5', width: 30, height: 30, borderRadius: '50%',
            cursor: 'pointer', fontSize: 16, color: '#6B7280', lineHeight: 1,
          }}>{'✕'}</button>
        </div>

        <div style={{
          overflowY: 'auto', flex: '1 1 auto', minHeight: 0,
          padding: '4px 12px',
          // Clearance SOLIDA in fondo (64px, non solo env) così l'ultima riga
          // ("Da evitare") non finisce mai sotto la toolbar del browser mobile
          // né sotto la home-bar. Marina 2026-07-12.
          paddingBottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
        }}>
          {/* Verdetto: lista a scelta singola con pallino colore */}
          {filter.type === 'verdetto' && rows.map((g) => {
            const active = (verdettoGroup && verdettoGroup.id === g.id);
            return (
              <button key={g.id} onClick={() => applyVerdetto(g)} style={rowStyle(active)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  {g.id !== 'tutti' && (
                    <span style={{ width: 11, height: 11, borderRadius: '50%', background: g.color }} />
                  )}
                  <span>{g.star ? '⭐ ' : ''}{g.label}</span>
                </span>
                {active && <Check />}
              </button>
            );
          })}

          {/* Single (prezzo/superficie): "Qualsiasi" + opzioni */}
          {filter.type === 'single' && (
            <>
              <button onClick={() => { setSingle(filter.key, ''); onClose(); }} style={rowStyle(!singleVal)}>
                <span>Qualsiasi</span>{!singleVal && <Check />}
              </button>
              {rows.map((o) => {
                const active = singleVal === o.v;
                return (
                  <button key={o.v} onClick={() => { setSingle(filter.key, o.v); onClose(); }} style={rowStyle(active)}>
                    <span>{o.label}</span>{active && <Check />}
                  </button>
                );
              })}
            </>
          )}

          {/* Date: input nativo "scade entro" */}
          {filter.type === 'date' && (
            <div style={{ padding: '10px 8px 6px' }}>
              <input
                type="date"
                value={params.get(filter.key) || ''}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setSingle(filter.key, e.target.value)}
                style={{
                  width: '100%', padding: '13px 14px', borderRadius: 12,
                  border: '1.5px solid #E4E6EA', background: '#fff',
                  fontSize: 15, fontWeight: 600, color: '#1F2937',
                  fontFamily: 'Manrope, system-ui, sans-serif',
                }}
              />
              <p style={{ margin: '10px 4px 0', fontSize: 12.5, color: '#8A9099' }}>
                Mostra solo le aste che scadono entro questa data.
              </p>
              <button
                onClick={() => { setSingle(filter.key, ''); onClose(); }}
                disabled={!params.get(filter.key)}
                style={{
                  marginTop: 14, width: '100%', padding: '11px 16px', borderRadius: 12,
                  border: '1.5px solid #E4E6EA', background: '#fff',
                  color: params.get(filter.key) ? '#3A3F45' : '#B9BEC5',
                  fontWeight: 700, fontSize: 14, cursor: params.get(filter.key) ? 'pointer' : 'default',
                }}
              >Azzera</button>
              <button
                onClick={onClose}
                style={{
                  marginTop: 10, width: '100%', padding: '11px 16px', borderRadius: 12, border: 0,
                  background: '#1F2937', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer',
                }}
              >Mostra risultati</button>
            </div>
          )}

          {/* Multi: chip toggle (applica live) */}
          {isMulti && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '4px 6px 8px' }}>
              {rows.map((o) => {
                const active = multiVals.includes(o.v);
                return (
                  <button key={o.v} onClick={() => toggleMulti(o.v)} style={{
                    padding: '9px 14px', borderRadius: 999, cursor: 'pointer',
                    fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap',
                    border: `1.5px solid ${active ? '#1F2937' : '#E4E6EA'}`,
                    background: active ? '#1F2937' : '#fff',
                    color: active ? '#fff' : '#3A3F45',
                  }}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* footer solo per multi: Cancella / Fatto */}
        {isMulti && (
          <div style={{
            display: 'flex', gap: 10, padding: '10px 16px',
            paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
            borderTop: '1px solid #EEF0F2',
          }}>
            <button onClick={() => setMulti(filter.key, [])} disabled={multiVals.length === 0} style={{
              flex: '0 0 auto', padding: '11px 16px', borderRadius: 12,
              border: '1.5px solid #E4E6EA', background: '#fff',
              color: multiVals.length ? '#3A3F45' : '#B9BEC5', fontWeight: 700, fontSize: 14,
              cursor: multiVals.length ? 'pointer' : 'default',
            }}>Cancella</button>
            <button onClick={onClose} style={{
              flex: 1, padding: '11px 16px', borderRadius: 12, border: 0,
              background: '#1F2937', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer',
            }}>Mostra risultati</button>
          </div>
        )}
      </div>
    </>
  );
}

const rowStyle = (active) => ({
  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '13px 14px', margin: '2px 0', borderRadius: 12, cursor: 'pointer',
  border: `1.5px solid ${active ? '#1F2937' : 'transparent'}`,
  background: active ? '#F8FAFC' : '#fff',
  fontSize: 15, fontWeight: 700, color: '#22272E', textAlign: 'left',
});

function Check() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1F2937" strokeWidth="3"
         strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
  );
}
